import { useEffect, useRef, useState } from "react";
import { useIntersectionObserver } from "../../../lib/use-intersection-observer";
import clsx from "clsx";
import styles from "./banner.module.css";
import {
  topBottomPaddingStyleObject,
  backgroundColorStyleObject,
} from "../../../lib/utilities";
const KEY = "Banner";
export type BannerData = {
  id?: string;
  backgroundColor?: string;
  reduceTopSpacing?: string;
  reduceBottomSpacing?: string;
  isBannerEnable?: boolean;
  descriptionHTML: { __html: string | TrustedHTML };
};

type TextProps = {
  descriptionHTML: BannerData["descriptionHTML"];
};

type BannerProps = {
  data: BannerData;
  animateIntoView?: boolean;
};

const Text = ({ descriptionHTML }: TextProps) => (
  <div className={clsx(styles.textContainer)}>
    <div
      className={styles.description}
      dangerouslySetInnerHTML={descriptionHTML}
    />
  </div>
);

const CloseIcon = () => (
  <svg
    width="40"
    height="40"
    viewBox="0 0 40 40"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M9.11612 9.11612C9.60427 8.62796 10.3957 8.62796 10.8839 9.11612L20 18.2322L29.1161 9.11612C29.6043 8.62796 30.3957 8.62796 30.8839 9.11612C31.372 9.60427 31.372 10.3957 30.8839 10.8839L21.7678 20L30.8839 29.1161C31.372 29.6043 31.372 30.3957 30.8839 30.8839C30.3957 31.372 29.6043 31.372 29.1161 30.8839L20 21.7678L10.8839 30.8839C10.3957 31.372 9.60427 31.372 9.11612 30.8839C8.62796 30.3957 8.62796 29.6043 9.11612 29.1161L18.2322 20L9.11612 10.8839C8.62796 10.3957 8.62796 9.60427 9.11612 9.11612Z"
      fill="#ffffff"
    />
  </svg>
);

export const Banner = ({ data, animateIntoView = true }: BannerProps) => {
  const {
    id,
    backgroundColor,
    reduceTopSpacing,
    reduceBottomSpacing,
    descriptionHTML,
    isBannerEnable,
  } = data;
  const ref = useRef<HTMLDivElement | null>(null);
  const viewportEntry = useIntersectionObserver(ref, {
    freezeOnceVisible: true,
  });
  const isVisible = !!viewportEntry?.isIntersecting;

  const [isBannerVisible, setIsBannerVisible] = useState(isBannerEnable);

  useEffect(() => {
    const alreadyViewed = JSON.parse(sessionStorage.getItem(KEY) || "true");
    setIsBannerVisible(isBannerEnable && alreadyViewed);
  }, [isBannerEnable]);

  const reducedPaddingCss = topBottomPaddingStyleObject(
    reduceTopSpacing,
    reduceBottomSpacing,
  );
  const backgroundColorCss = backgroundColorStyleObject(backgroundColor);

  const closeBannerHandler = () => {
    setIsBannerVisible(false);
    sessionStorage.setItem(KEY, "false");
  };

  return (
    <section ref={ref} id={id ?? undefined} className={styles.container}>
      {isBannerVisible && (
        <div
          className={clsx(styles.outerLayout)}
          style={{ ...reducedPaddingCss, ...backgroundColorCss }}
        >
          <div
            className={clsx(
              "defaultLayoutOuter defaultGrid",
              styles.outerContainer,
              {
                defaultAnimatableIntoView: animateIntoView,
                defaultPlayAnimationIntoView: animateIntoView && isVisible,
              },
            )}
            data-testid="banner"
          >
            <Text descriptionHTML={descriptionHTML} />
            <div className={styles.close} onClick={() => closeBannerHandler()}>
              <CloseIcon></CloseIcon>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};
